import { FC, ReactNode } from "react";
import Link from "next/link";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShareLinkButton from "@/components/share-link-button/ShareLinkButton";
import styles from "./MarketsPulse.module.scss";

export type DetailFact = { label: string; value: ReactNode; tone?: "up" | "down" };

export type DetailContent = {
    title: string;
    tag?: ReactNode;
    facts?: DetailFact[];
    body?: ReactNode;
    links?: { label: string; href: string; primary?: boolean }[];
};

/**
 * The drill-in every card and row on the front page opens: key figures, a few lines of context, links out.
 * `shareTitle` marks a deep-linked item (the URL is its link) and adds the share button.
 * `onLeave` runs instead of `onClose` when a link out is followed, so a linked item's URL is
 * left alone and Back returns to it.
 */
const DetailDialog: FC<{ content: DetailContent | null; onClose: () => void; onLeave?: () => void; shareTitle?: string }> = ({ content, onClose, onLeave, shareTitle }) => (
    <Dialog open={!!content} onClose={onClose} maxWidth="sm" fullWidth>
        {content && (
            <>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: shareTitle ? 11 : 6 }}>
                    {content.tag}
                    <span>{content.title}</span>
                    <span style={{ position: "absolute", right: 8, top: 8, display: "flex", gap: 4 }}>
                        {shareTitle && <ShareLinkButton title={shareTitle} size="medium" />}
                        <IconButton aria-label="Close" onClick={onClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </span>
                </DialogTitle>
                <DialogContent dividers>
                    <div className={styles.dialogBody}>
                        {content.facts?.length ? (
                            <div className={styles.kv}>
                                {content.facts.map((fact) => (
                                    <div key={fact.label}>
                                        <span>{fact.label}</span>
                                        <b className={fact.tone ? styles[fact.tone] : undefined}>{fact.value}</b>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                        {content.body}
                    </div>
                </DialogContent>
                {content.links?.length ? (
                    <DialogActions>
                        {content.links.map((link) => (
                            <Link passHref legacyBehavior key={link.href + link.label} href={link.href}>
                                <Button component="a" variant={link.primary ? "contained" : "outlined"} size="small" onClick={onLeave || onClose}>
                                    {link.label}
                                </Button>
                            </Link>
                        ))}
                    </DialogActions>
                ) : null}
            </>
        )}
    </Dialog>
);

export default DetailDialog;
